/**
 * Helpers for AIAgentSchedule AccessControl + Collaborators.
 *
 * Unlike agents/apps/lists (dedicated `/config/collaborators` routes),
 * schedules embed both fields on the entity and mutate them via
 * PATCH `/ai/schedule/{id}` (or full PUT on create/update).
 *
 * Note: `canManage` is NOT an OData property on AIAgentSchedule — derive
 * it with `scheduleCanManage` from ownerUserId + collaborators + admin.
 */

import { isTenantAdmin } from '@/pages/Launchpad/utils/permissions';
import { getAll } from '@/services/api';

/**
 * Who may edit / activate / configure sharing for a schedule.
 * Matrix: owner, schedule collaborator, tenant admin/superadmin.
 * AccessControl-only audience → false (view/run/history only).
 */
export const scheduleCanManage = (item, currentUserId, roles) => {
  if (isTenantAdmin(roles)) return true;
  if (!item) return false;

  const raw = item.raw && (item.raw.id != null || item.raw.ownerUserId != null) ? item.raw : item;
  const ownerId = item.ownerUserId ?? raw?.ownerUserId ?? null;
  const collaborators = Array.isArray(item.collaborators)
    ? item.collaborators
    : Array.isArray(raw?.collaborators)
    ? raw.collaborators
    : null;

  const uid = currentUserId != null && currentUserId !== '' ? String(currentUserId) : null;
  if (uid && ownerId != null && String(ownerId) === uid) return true;
  if (
    uid &&
    Array.isArray(collaborators) &&
    collaborators.some((c) => c?.id != null && String(c.id) === uid)
  ) {
    return true;
  }

  // Sharing is not projected on OData $select list payloads (`collaborators`
  // stays null). Don't lock collaborator editors until a full GET hydrates it.
  if (collaborators == null) return true;
  return false;
};

/** True when accessControl grants no extra audience. */
export const isAccessControlEmpty = (accessControl) => {
  if (!accessControl || typeof accessControl !== 'object') return true;
  const buckets = ['users', 'userGroups', 'positions', 'areas'];
  return buckets.every(
    (key) => !Array.isArray(accessControl[key]) || accessControl[key].length === 0,
  );
};

/**
 * Normalize schedule collaborators (`BaseConfigSimpleUser[]`) for the
 * shared CollaboratorsDrawer (expects `fullName` / `names` for display).
 */
export const normalizeScheduleCollaborators = (list) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  list.forEach((c) => {
    const id = c?.id;
    if (!id || seen.has(id)) return;
    seen.add(id);
    const name = c.name || c.fullName || c.names || '';
    out.push({
      id,
      name,
      email: c.email || '',
      fullName: name,
    });
  });
  return out;
};

/** Strip display-only fields before sending to the API. */
export const toBaseConfigSimpleUsers = (list) =>
  normalizeScheduleCollaborators(list).map(({ id, name, email }) => ({
    id,
    name: name || '',
    email: email || '',
  }));

/**
 * Resolve user ids → BaseConfigSimpleUser rows for PATCH/POST.
 * One batched `user/paged` call.
 */
export async function resolveUsersAsCollaborators(userIds) {
  const ids = Array.from(new Set((userIds || []).filter(Boolean)));
  if (!ids.length) return [];

  try {
    const filter = ids.map((id) => `id eq ${id}`).join(' or ');
    const res = await getAll({
      route: 'user/paged',
      select: 'id,fullName,names,lastNames,email',
      filter,
      limit: ids.length,
    });
    const items = res?.items || res?.data?.items || [];
    const byId = (Array.isArray(items) ? items : []).reduce((acc, u) => {
      if (u?.id) acc[u.id] = u;
      return acc;
    }, {});

    return ids.map((id) => {
      const u = byId[id];
      const name = u?.fullName || [u?.names, u?.lastNames].filter(Boolean).join(' ') || '';
      return {
        id,
        name,
        email: u?.email || '',
        fullName: name,
      };
    });
  } catch {
    return ids.map((id) => ({ id, name: '', email: '', fullName: '' }));
  }
}

/** PATCH payload: empty AC → null (clears); otherwise the normalized block. */
export const accessControlPatchValue = (accessControl) => {
  if (isAccessControlEmpty(accessControl)) return null;
  return {
    filtersLogicalOperator: accessControl.filtersLogicalOperator || 'or',
    userGroupsFiltersLogicalOperator: accessControl.userGroupsFiltersLogicalOperator || 'or',
    users: Array.isArray(accessControl.users) ? accessControl.users : [],
    userGroups: Array.isArray(accessControl.userGroups) ? accessControl.userGroups : [],
    positions: Array.isArray(accessControl.positions) ? accessControl.positions : [],
    areas: Array.isArray(accessControl.areas) ? accessControl.areas : [],
  };
};
